import { useState } from "react";
import { Plus, Trash2, Upload, X, Camera, Share2 } from "lucide-react";
import { toast } from "sonner";
import { shareOrDownloadFile } from "@/lib/share";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Field, inputCls } from "@/components/shared";

export const sectionsTotal = (sections) =>
  Math.round((sections || []).reduce((s, x) => s + (Number(x.length) || 0) * (Number(x.width) || 0), 0) * 10) / 10;

export const roomArea = (room) => {
  if (room.area_manual != null && room.area_manual !== "") return Number(room.area_manual);
  if (room.sketch?.area) return Number(room.sketch.area);
  return sectionsTotal(room.sections);
};

const FLOORS = ["Basement", "Ground floor", "First floor", "Second floor", "Attic", "Outbuilding"];
const SHAPES = ["rectangle", "l_shape", "irregular", "manual"];

const emptyRoom = {
  name: "", floor: "Ground floor", description: "", shape_type: "rectangle",
  ceiling_height: "", flooring: "", heating: "", windows: "", built_in_storage: "",
  condition: "", notes: "", photos: [], sections: [], sketch: null,
  area_manual: "", exclude_habitable: false, needs_verification: false,
};

export const RoomDialog = ({ room, propertyId, existingCount, onClose, onSaved }) => {
  const { t } = useLang();
  const [form, setForm] = useState({
    ...emptyRoom,
    ...(room || {}),
    ceiling_height: room?.ceiling_height ?? "",
    area_manual: room?.area_manual ?? "",
    sections: room?.sections?.length
      ? room.sections.map((s) => ({ ...s }))
      : room?.id
        ? []
        : [{ id: crypto.randomUUID(), name: "Section A", length: "", width: "" }],
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const num = (v) => (v === "" || v == null ? null : Number(v));

  const secTotal = sectionsTotal(form.sections);
  const finalArea = form.area_manual !== "" ? Number(form.area_manual) : (form.sketch?.area ?? secTotal);

  const updateSection = (i, patch) =>
    setForm({ ...form, sections: form.sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) });
  const addSection = () =>
    setForm({
      ...form,
      sections: [...form.sections, {
        id: crypto.randomUUID(),
        name: `Section ${String.fromCharCode(65 + form.sections.length)}`,
        length: "", width: "",
      }],
    });
  const removeSection = (i) => setForm({ ...form, sections: form.sections.filter((_, idx) => idx !== i) });
  const moveSection = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= form.sections.length) return;
    const next = [...form.sections];
    [next[i], next[j]] = [next[j], next[i]];
    setForm({ ...form, sections: next });
  };

  const uploadPhoto = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photos: [...(f.photos || []), data.path] }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("required"));
    setSaving(true);
    const payload = {
      ...form,
      property_id: propertyId,
      ceiling_height: num(form.ceiling_height),
      area_manual: num(form.area_manual),
      sections: form.sections
        .filter((s) => s.length !== "" || s.width !== "" || s.name)
        .map((s) => ({ ...s, length: Number(s.length) || 0, width: Number(s.width) || 0 })),
      area: finalArea,
      sort_order: room?.sort_order ?? existingCount,
    };
    try {
      if (room?.id) await api.put(`/rooms/${room.id}`, payload);
      else await api.post("/rooms", payload);
      toast.success(t("saved"));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const shapeLabel = { rectangle: "Rectangle", l_shape: "L-shape", irregular: "Irregular", manual: "Manual" };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-medium text-stone-900">
            {room?.id ? t("editRoom") : t("addRoom")}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <Field label={t("roomName")}>
            <input data-testid="room-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Kitchen, Bedroom 1…" />
          </Field>
          <Field label={t("floorLevel")}>
            <Select value={form.floor || "Ground floor"} onValueChange={(v) => setForm({ ...form, floor: v })}>
              <SelectTrigger data-testid="room-floor-select" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                {FLOORS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("descriptionLabel")} className="sm:col-span-2">
            <textarea data-testid="room-description-input" rows={2} className={inputCls} value={form.description} onChange={set("description")} />
          </Field>
          <Field label={t("shapeType")}>
            <Select value={form.shape_type} onValueChange={(v) => setForm({ ...form, shape_type: v })}>
              <SelectTrigger data-testid="room-shape-select" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                {SHAPES.map((s) => <SelectItem key={s} value={s}>{shapeLabel[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("ceilingHeight")}>
            <input data-testid="room-ceiling-input" type="number" step="0.01" className={inputCls} value={form.ceiling_height} onChange={set("ceiling_height")} />
          </Field>
          <Field label={t("flooring")}><input className={inputCls} value={form.flooring} onChange={set("flooring")} /></Field>
          <Field label={t("heating")}><input className={inputCls} value={form.heating} onChange={set("heating")} /></Field>
          <Field label={t("windowsGlazing")}><input className={inputCls} value={form.windows} onChange={set("windows")} /></Field>
          <Field label={t("builtInStorage")}><input className={inputCls} value={form.built_in_storage} onChange={set("built_in_storage")} /></Field>
          <Field label={t("condition")}><input className={inputCls} value={form.condition} onChange={set("condition")} /></Field>
          <Field label={t("notes")}><input data-testid="room-notes-input" className={inputCls} value={form.notes} onChange={set("notes")} /></Field>
        </div>

        {/* Measurement sections */}
        <Field label={t("sectionsLabel")} className="mt-4">
          <div className="space-y-2">
            {form.sections.map((s, i) => {
              const area = Math.round((Number(s.length) || 0) * (Number(s.width) || 0) * 10) / 10;
              return (
                <div key={s.id} data-testid={`room-section-row-${i}`} className="flex flex-wrap items-center gap-2 bg-stone-50 border border-stone-200 rounded-lg p-2.5">
                  <input data-testid={`section-name-${i}`} className={`${inputCls} w-32 flex-1 min-w-[100px]`} placeholder={t("sectionName")}
                    value={s.name} onChange={(e) => updateSection(i, { name: e.target.value })} />
                  <input data-testid={`section-length-${i}`} type="number" step="0.01" className={`${inputCls} w-24`} placeholder={t("lengthM")}
                    value={s.length} onChange={(e) => updateSection(i, { length: e.target.value })} />
                  <span className="text-stone-400 text-xs">×</span>
                  <input data-testid={`section-width-${i}`} type="number" step="0.01" className={`${inputCls} w-24`} placeholder={t("widthM")}
                    value={s.width} onChange={(e) => updateSection(i, { width: e.target.value })} />
                  <span data-testid={`section-area-${i}`} className="text-sm font-medium text-stone-700 whitespace-nowrap min-w-[64px] text-right">
                    {area.toFixed(1)} m²
                  </span>
                  <div className="flex items-center">
                    <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="text-stone-300 hover:text-stone-700 disabled:opacity-30 px-1">↑</button>
                    <button onClick={() => moveSection(i, 1)} disabled={i === form.sections.length - 1} className="text-stone-300 hover:text-stone-700 disabled:opacity-30 px-1">↓</button>
                    <button data-testid={`section-delete-${i}`} onClick={() => removeSection(i)} className="text-stone-400 hover:text-[#7F1D1D] p-1">
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              );
            })}
            <button data-testid="add-section-button" onClick={addSection}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-600 hover:text-stone-900 border border-dashed border-stone-300 hover:border-stone-400 rounded-lg px-3 py-2 transition-colors">
              <Plus size={13} strokeWidth={1.5} /> {t("addSection")}
            </button>
          </div>
        </Field>

        <div className="flex flex-wrap items-center gap-5 bg-stone-50 border border-stone-200 rounded-lg p-4 mt-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-stone-500">{t("totalArea")}</p>
            <p data-testid="room-total-area" className="font-heading text-2xl font-medium text-stone-900">{finalArea.toFixed(1)} m²</p>
            {form.sketch?.area ? <p className="text-[11px] text-stone-400">{t("sketchRoom")}: {Number(form.sketch.area).toFixed(1)} m²</p> : null}
            {form.sections.length > 0 && <p className="text-[11px] text-stone-400">{t("sectionsLabel")}: {secTotal.toFixed(1)} m²</p>}
          </div>
          <div className="ml-auto">
            <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-stone-500 mb-1">{t("overrideArea")}</p>
            <input data-testid="room-override-input" type="number" step="0.1" className={`${inputCls} w-28`}
              value={form.area_manual} onChange={set("area_manual")} placeholder="—" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-6 mt-4">
          <label className="flex items-center gap-2.5 text-sm text-stone-700 cursor-pointer">
            <Checkbox data-testid="room-exclude-checkbox" checked={form.exclude_habitable}
              onCheckedChange={(c) => setForm({ ...form, exclude_habitable: !!c })} />
            {t("excludeHabitable")}
          </label>
          <label className="flex items-center gap-2.5 text-sm text-stone-700 cursor-pointer">
            <Checkbox data-testid="room-verify-checkbox" checked={form.needs_verification}
              onCheckedChange={(c) => setForm({ ...form, needs_verification: !!c })} />
            {t("needsVerification")}
          </label>
        </div>

        {/* Photos */}
        <Field label={t("photos")} className="mt-4">
          <div className="flex flex-wrap gap-2">
            {(form.photos || []).map((p, i) => (
              <div key={p} className="relative group">
                <img src={`${API}/files/${p}`} alt="" className="h-20 w-28 object-cover rounded-lg border border-stone-200" />
                <button data-testid={`room-photo-share-${i}`}
                  onClick={async () => {
                    try {
                      const r = await shareOrDownloadFile(p, `room-photo-${i + 1}.${p.split(".").pop()}`);
                      if (r === "downloaded") toast.info(t("fileDownloaded"));
                    } catch { toast.error("Share failed"); }
                  }}
                  title={t("share")}
                  className="absolute -top-1.5 -left-1.5 bg-slate-900 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Share2 size={12} />
                </button>
                <button data-testid={`room-photo-remove-${i}`}
                  onClick={() => setForm({ ...form, photos: form.photos.filter((x) => x !== p) })}
                  className="absolute -top-1.5 -right-1.5 bg-slate-900 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <X size={12} />
                </button>
              </div>
            ))}
            <label className={`h-20 w-28 flex flex-col items-center justify-center gap-1 border border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-stone-400 text-stone-400 hover:text-stone-600 transition-colors text-[11px] ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
              <Upload size={16} strokeWidth={1.5} />
              {uploading ? "…" : t("add")}
              <input data-testid="room-photo-upload-input" type="file" accept="image/*" className="hidden"
                onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} />
            </label>
            {/* Direct-to-camera on iPhone/iPad (capture is ignored on desktop) */}
            <label className={`h-20 w-28 flex flex-col items-center justify-center gap-1 border border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-stone-400 text-stone-400 hover:text-stone-600 transition-colors text-[11px] ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
              <Camera size={16} strokeWidth={1.5} />
              {uploading ? "…" : t("takePhoto")}
              <input data-testid="room-photo-camera-input" type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} />
            </label>
          </div>
        </Field>

        <div className="flex justify-end gap-3 mt-6">
          <button data-testid="cancel-room-button" onClick={onClose}
            className="bg-white text-stone-700 border border-stone-200 hover:bg-stone-50 rounded-lg px-5 py-2 text-sm font-medium transition-colors">{t("cancel")}</button>
          <button data-testid="save-room-button" onClick={save} disabled={saving}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2 text-sm font-medium transition-colors disabled:opacity-60">{t("save")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
